import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useBlocker, useNavigate } from 'react-router-dom';

// Global "unsaved changes" guard.
//
// Pages call useUnsavedChanges(isDirty, message) to register that they
// currently hold unsaved edits. Before any navigation that would discard
// those edits (sidebar Link click, programmatic confirmNavigation call, or
// the browser tab unloading) we surface a confirmation modal / native
// beforeunload prompt so the user does not lose work accidentally.
//
// We intentionally keep this lightweight: pages own their save logic, this
// context only owns the "is anything dirty?" registry and the confirmation UI.

const noop = () => {};

const UnsavedChangesContext = createContext({
  registerDirty: noop,
  isAnyDirty: () => false,
  confirmDiscard: (onConfirm) => onConfirm(),
});

const DEFAULT_MESSAGE =
  'You have unsaved changes. If you leave now, your changes will be lost.';

export function UnsavedChangesProvider({ children }) {
  // Map<id, { dirty: boolean, message: string }>
  const dirtyRegistryRef = useRef(new Map());
  // Bumped whenever the registry changes so the beforeunload effect re-binds
  // with the latest state. We don't render anything from this counter.
  const [, setRegistryVersion] = useState(0);
  const [pendingNav, setPendingNav] = useState(null);

  const updateVersion = useCallback(() => {
    setRegistryVersion((v) => v + 1);
  }, []);

  const registerDirty = useCallback(
    (id, dirty, message) => {
      if (dirty) {
        dirtyRegistryRef.current.set(id, {
          dirty: true,
          message: message || DEFAULT_MESSAGE,
        });
      } else {
        dirtyRegistryRef.current.delete(id);
      }
      updateVersion();
    },
    [updateVersion]
  );

  const isAnyDirty = useCallback(() => dirtyRegistryRef.current.size > 0, []);

  const getActiveMessage = useCallback(() => {
    const first = dirtyRegistryRef.current.values().next().value;
    return first?.message || DEFAULT_MESSAGE;
  }, []);

  const confirmDiscard = useCallback(
    (onConfirm, onCancel) => {
      if (!isAnyDirty()) {
        onConfirm();
        return;
      }
      setPendingNav({ onConfirm, onCancel });
    },
    [isAnyDirty]
  );

  // Browser tab close / refresh guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirtyRegistryRef.current.size === 0) return;
      e.preventDefault();
      // Most modern browsers ignore the custom message and show their own,
      // but returnValue must still be set to trigger the prompt.
      e.returnValue = getActiveMessage();
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [getActiveMessage]);

  const handleConfirm = useCallback(() => {
    const { onConfirm } = pendingNav || {};
    setPendingNav(null);
    // Wipe the registry so subsequent navigations don't immediately re-prompt
    // for changes the user just chose to discard. Pages re-register from their
    // own state when they remount or rerender.
    dirtyRegistryRef.current.clear();
    updateVersion();
    if (typeof onConfirm === 'function') onConfirm();
  }, [pendingNav, updateVersion]);

  const handleCancel = useCallback(() => {
    const { onCancel } = pendingNav || {};
    setPendingNav(null);
    if (typeof onCancel === 'function') onCancel();
  }, [pendingNav]);

  const value = useMemo(
    () => ({ registerDirty, isAnyDirty, confirmDiscard }),
    [registerDirty, isAnyDirty, confirmDiscard]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {pendingNav && (
        <UnsavedChangesModal
          message={getActiveMessage()}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </UnsavedChangesContext.Provider>
  );
}

function UnsavedChangesModal({ message, onConfirm, onCancel }) {
  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC cancels, Enter confirms
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, onConfirm]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-title"
        style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          maxWidth: '440px',
          width: '100%',
          padding: '24px 24px 20px',
          fontFamily: 'inherit',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#fef3c7',
              color: '#b45309',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            !
          </div>
          <h3
            id="unsaved-changes-title"
            style={{
              margin: 0,
              fontSize: '17px',
              fontWeight: 700,
              color: '#1f2937',
            }}
          >
            Unsaved changes
          </h3>
        </div>
        <p
          style={{
            margin: '0 0 20px 4px',
            color: '#4b5563',
            fontSize: '14px',
            lineHeight: 1.5,
          }}
        >
          {message}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              border: '1.5px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stay on page
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              border: '1.5px solid #dc2626',
              background: '#dc2626',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Discard changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook for pages/forms to register their dirty state. Pass `dirty=true` while
// the user has uncommitted edits, and `dirty=false` after a successful save
// (or when the user discards). Also blocks browser back/forward and any
// programmatic in-app navigation while dirty by hooking react-router's
// useBlocker and routing it through the same confirmation modal.
export function useUnsavedChanges(dirty, message) {
  const { registerDirty, confirmDiscard } = useContext(UnsavedChangesContext);
  const idRef = useRef(null);
  if (idRef.current === null) {
    idRef.current = `uc-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }

  useEffect(() => {
    registerDirty(idRef.current, !!dirty, message);
  }, [dirty, message, registerDirty]);

  useEffect(() => {
    const id = idRef.current;
    return () => registerDirty(id, false);
  }, [registerDirty]);

  // Block in-app navigation (browser back/forward, anything that goes through
  // react-router's history). GuardedLink already pre-empts most sidebar nav
  // before it ever reaches the router, but this is the safety net.
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      !!dirty && currentLocation.pathname !== nextLocation.pathname,
    [dirty]
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!blocker || blocker.state !== 'blocked') return;
    confirmDiscard(
      () => blocker.proceed && blocker.proceed(),
      () => blocker.reset && blocker.reset()
    );
  }, [blocker, confirmDiscard]);
}

// Imperative helper for pages that need to gate their own internal navigation
// (e.g. switching dates, opening a different record) on the unsaved-change
// modal.
export function useConfirmDiscardChanges() {
  const { confirmDiscard, isAnyDirty } = useContext(UnsavedChangesContext);
  return { confirmDiscard, isAnyDirty };
}

// Drop-in replacement for react-router's <Link>. If anything in the app is
// dirty, clicking it surfaces the confirmation modal first; otherwise it
// behaves exactly like a normal Link.
export function GuardedLink({ to, onClick, children, ...rest }) {
  const { isAnyDirty, confirmDiscard } = useContext(UnsavedChangesContext);
  const navigate = useNavigate();

  const handleClick = (e) => {
    // Let modifier-clicks (open in new tab, etc.) pass through unchanged.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      if (typeof onClick === 'function') onClick(e);
      return;
    }

    if (isAnyDirty()) {
      e.preventDefault();
      confirmDiscard(() => {
        navigate(to);
        if (typeof onClick === 'function') onClick(e);
      });
      return;
    }

    if (typeof onClick === 'function') onClick(e);
  };

  return (
    <Link to={to} onClick={handleClick} {...rest}>
      {children}
    </Link>
  );
}

export default UnsavedChangesContext;
